
import React, { useEffect, useRef } from "react";
import { Mic, Speaker } from "lucide-react";
import { useElevenLabsScreeningAgent } from "@/hooks/tools/useElevenLabsScreeningAgent";

interface LiveTranscriptProps {
  transcript: string;
  isLive?: boolean;
}

export const LiveTranscript: React.FC<LiveTranscriptProps> = ({ transcript, isLive = false }) => {
  const transcriptRef = useRef<HTMLDivElement>(null);
  const { synthesizeSpeech, isProcessing } = useElevenLabsScreeningAgent();
  
  const lastSpeakerLineRef = useRef<string>("");
  const timeoutRef = useRef<number | null>(null);
  
  // Auto-scroll to the bottom when transcript updates
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
    
    // If this is a live transcript and we have new interviewer text, synthesize speech
    if (isLive && transcript) {
      const lines = transcript.split('\n');
      const interviewerLines = lines.filter(line => line.startsWith('Interviewer:'));
      
      if (interviewerLines.length > 0) {
        const lastLine = interviewerLines[interviewerLines.length - 1];
        
        // Only synthesize if this is a new line
        if (lastLine !== lastSpeakerLineRef.current) {
          lastSpeakerLineRef.current = lastLine;
          
          // Extract just the speech part (after "Interviewer: ")
          const speechText = lastLine.replace('Interviewer: ', '').trim();
          
          // Clear any pending timeout
          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
          }
          
          // Add a small delay to avoid multiple rapid speech synthesis calls
          timeoutRef.current = window.setTimeout(() => {
            synthesizeSpeech(speechText);
          }, 300);
        }
      }
    }
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [transcript, isLive, synthesizeSpeech]);
  
  if (!transcript) {
    return (
      <div className="border rounded-md p-4 bg-muted/50 h-[200px] flex items-center justify-center">
        <p className="text-muted-foreground">Waiting for call to begin...</p>
      </div>
    );
  }
  
  return (
    <div 
      ref={transcriptRef}
      className="border rounded-md p-4 h-[200px] overflow-y-auto text-sm"
    >
      <div className="space-y-2">
        {transcript.split('\n').map((line, i) => {
          // Simple heuristic to identify speaker - in a real app, the API would return this info
          const isCandidateResponse = line.startsWith('Candidate:');
          const isInterviewer = line.startsWith('Interviewer:');
          
          return (
            <div key={i} className={`flex items-start gap-2 ${isInterviewer ? 'text-blue-600' : isCandidateResponse ? 'text-green-600' : ''}`}>
              {isInterviewer && <Speaker className="h-4 w-4 mt-0.5 flex-shrink-0" />}
              {isCandidateResponse && <Mic className="h-4 w-4 mt-0.5 flex-shrink-0" />}
              <span className={`${isInterviewer || isCandidateResponse ? 'font-medium' : ''}`}>
                {line}
              </span>
            </div>
          );
        })}
        
        {isLive && (
          <div className="animate-pulse flex items-center gap-2">
            <Speaker className="h-4 w-4 text-blue-600" />
            <span className="text-muted-foreground">
              {isProcessing ? "Speaking..." : "..."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
